import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  doc, 
  onSnapshot, 
  collection, 
  query, 
  orderBy, 
  setDoc, 
  updateDoc, 
  increment, 
  serverTimestamp, 
  getDoc 
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import ImportStyle from './importstyle'; 

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function Styles() {
  // 3D & User State
  const [hasScan, setHasScan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  
  // Library State
  const [styles, setStyles] = useState([]);
  const [globalStyles, setGlobalStyles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImport, setShowImport] = useState(false);
  
  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) { navigate('/login'); return; }

    // 1. CHECK 3D SCAN STATUS
    const checkScan = async () => {
      const snap = await getDoc(doc(db, 'users', user.uid));
      if (snap.exists()) {
        const data = snap.data();
        setUserData(data);
        setHasScan(data.hasCompletedScan || false);
      }
      setLoading(false);
    };
    checkScan();

    // 2. YOUR VAULT (Favorites & Personal Imports)
    const personalStylesRef = collection(db, 'users', user.uid, 'customStyles');
    const unsubStyles = onSnapshot(personalStylesRef, (snap) => {
      setStyles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // 3. COMMUNITY TRENDS
    const globalRef = collection(db, 'globalGallery');
    const q = query(globalRef, orderBy('votes', 'desc'));
    const unsubGlobal = onSnapshot(q, (snap) => {
      setGlobalStyles(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => { unsubStyles(); unsubGlobal(); };
  }, [user, navigate]);

  // Logic to "Favorite" a style from Global to Personal Vault
  const favoriteStyle = async (e, style) => {
    e.stopPropagation();
    try {
      // Increment global votes
      await updateDoc(doc(db, 'globalGallery', style.id), { votes: increment(1) });
      
      // Save to User's Personal Collection
      const userStyleRef = doc(db, 'users', user.uid, 'customStyles', style.id);
      await setDoc(userStyleRef, {
        ...style,
        savedAt: serverTimestamp(),
        isFavorite: true
      });
      alert("Added to your Vault! You can now try this on your 3D mesh.");
    } catch (err) { 
      console.error(err);
      alert("Error saving style"); 
    }
  };

  const filteredVault = styles.filter(s => s.name?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredGlobal = globalStyles.filter(s => s.name?.toLowerCase().includes(searchQuery.toLowerCase()));

  if (loading) return <div style={loaderStyle}>Synchronizing 3D Vault...</div>;

  return (
    <div style={{ minHeight: '100vh', background: NAVY, color: 'white', padding: '20px', paddingBottom: '100px', fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header & Logo */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <img src="/icons/esdras-logo.png" style={{ width: '80px' }} alt="Esdras" />
      </div>

      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h1 style={{ color: GOLD, fontSize: '1.2rem', margin: 0 }}>STYLE ARCHITECTURE</h1>
        <div style={badgeStyle}>{hasScan ? '3D ENABLED' : '2D PREVIEW'}</div>
      </header>

      {/* 3D VIEWER HERO SECTION */}
      <section style={viewerContainer}>
        {hasScan ? (
          <div style={placeholder3D} onClick={() => navigate('/viewer/default')}>
            <div style={pulse}></div>
            <p style={{ fontWeight: 'bold', letterSpacing: '2px' }}>3D BIOMETRIC MESH ACTIVE</p>
            <p style={{ fontSize: '0.7rem', opacity: 0.5 }}>Tap to enter full immersion</p>
          </div>
        ) : (
          <div style={noScanBox}>
            <p style={{ marginBottom: '10px' }}>3D Biometrics Not Found</p>
            <button onClick={() => navigate('/scan')} style={miniBtn}>START 3D SCAN</button>
          </div>
        )}
      </section>

      {/* SEARCH BAR */}
      <input 
        placeholder="Search styles..." 
        value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
        style={searchBar}
      />

      {/* PERSONAL VAULT SECTION */}
      <h3 style={label}>YOUR PERSONAL VAULT (3D READY)</h3>
      <div style={grid}>
        {filteredVault.length > 0 ? filteredVault.map(s => (
          <div key={s.id} onClick={() => navigate(`/viewer/${s.id}`)} style={card}>
            <img src={s.image || s.imageUrl} style={imgStyle} alt={s.name} />
            <p style={nameStyle}>{s.name}</p>
          </div>
        )) : (
          <p style={{ fontSize: '0.8rem', opacity: 0.5, gridColumn: '1/-1', textAlign: 'center', padding: '20px' }}>
            No personal styles saved yet.
          </p>
        )}
      </div>

      {/* GLOBAL TRENDS SECTION */}
      <h3 style={{ ...label, marginTop: '40px' }}>COMMUNITY TRENDS</h3>
      <div style={grid}>
        {filteredGlobal.map(s => (
          <div key={s.id} onClick={() => navigate(`/viewer/${s.id}`)} style={card}>
            <div style={voteBadge} onClick={(e) => favoriteStyle(e, s)}>
               ❤️ {s.votes || 0}
            </div>
            <img src={s.image || s.imageUrl} style={imgStyle} alt={s.name} />
            <div style={{ padding: '10px' }}>
              <p style={nameStyle}>{s.name}</p>
              <p style={authorStyle}>By {s.sharedBy || 'Stylist'}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Floating Action Button for Imports */}
      <button onClick={() => setShowImport(true)} style={fab}>+</button>
      
      {showImport && (
        <ImportStyle 
          onClose={() => setShowImport(false)} 
          onStyleImported={() => setShowImport(false)} 
        />
      )}
    </div>
  );
}

// --- STYLES ---
const loaderStyle = { height: '100vh', display: 'grid', placeItems: 'center', background: NAVY, color: GOLD, fontWeight: 'bold' };

const badgeStyle = { 
  background: GOLD, 
  color: 'black', 
  padding: '4px 12px', 
  borderRadius: '5px', 
  fontSize: '0.65rem', 
  fontWeight: 'bold' 
};

const viewerContainer = { 
  width: '100%', 
  height: '250px', 
  background: 'rgba(0,0,0,0.4)', 
  borderRadius: '25px', 
  margin: '10px 0 30px', 
  overflow: 'hidden', 
  border: `1px solid ${GOLD}44`,
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

const placeholder3D = { 
  textAlign: 'center', 
  cursor: 'pointer',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
};

const pulse = {
  width: '15px',
  height: '15px',
  background: GOLD,
  borderRadius: '50%',
  margin: '0 auto 10px',
  boxShadow: `0 0 15px ${GOLD}`
};

const noScanBox = { textAlign: 'center', padding: '20px' };

const miniBtn = { 
  background: GOLD, 
  border: 'none', 
  padding: '10px 20px', 
  borderRadius: '50px', 
  fontWeight: 'bold', 
  fontSize: '0.8rem',
  cursor: 'pointer' 
};

const searchBar = { 
  width: '100%', 
  padding: '15px 25px', 
  borderRadius: '50px', 
  background: 'rgba(255,255,255,0.05)', 
  color: 'white', 
  border: `1px solid ${GOLD}66`, 
  marginBottom: '30px', 
  outline: 'none',
  fontSize: '1rem'
};

const label = { color: GOLD, fontSize: '0.7rem', letterSpacing: '3px', fontWeight: 'bold', marginBottom: '15px' };

const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' };

const card = { 
  background: 'rgba(255,255,255,0.03)', 
  borderRadius: '18px', 
  overflow: 'hidden', 
  textAlign: 'left', 
  position: 'relative',
  border: '1px solid rgba(255,255,255,0.05)'
};

const imgStyle = { width: '100%', height: '140px', objectFit: 'cover' };

const nameStyle = { margin: 0, fontSize: '0.85rem', fontWeight: 'bold' };

const authorStyle = { fontSize: '0.65rem', color: GOLD, marginTop: '4px' };

const voteBadge = { 
  position: 'absolute', 
  top: '10px', 
  right: '10px', 
  background: 'rgba(0,0,0,0.6)', 
  backdropFilter: 'blur(5px)',
  color: 'white', 
  padding: '5px 10px', 
  borderRadius: '20px', 
  fontSize: '0.6rem', 
  fontWeight: 'bold', 
  zIndex: 2,
  border: `1px solid ${GOLD}`
};

const fab = { 
  position: 'fixed', 
  bottom: '30px', 
  right: '30px', 
  background: GOLD, 
  width: '60px', 
  height: '60px', 
  borderRadius: '50%', 
  border: 'none', 
  fontSize: '2rem',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
  cursor: 'pointer',
  zIndex: 10
};
    
